import React from 'react';
import ClientFeatureConfigService from '../services/clientFeatureConfig.services';
import FeatureService from '../services/feature.services';
import { ClientFeatureConfig, NewClientFeatureConfig, Feature, FeatureInterfaceBase } from '../types/Feature';
import ToastNotification from '../utils/ToastNotification';

const ClientFeatureConfigController = class ClientFeatureConfigController {
    public async getAllFeatureConfigByClientId(
        clientId: number,
        featureName?: string,
    ): Promise<ClientFeatureConfig<FeatureInterfaceBase>[]> {
        const clientFeatureConfig: ClientFeatureConfig<
            FeatureInterfaceBase
        >[] = await ClientFeatureConfigService.getAllFeatureConfigByClientId(clientId, featureName);
        return clientFeatureConfig;
    }

    public async getClientFeatureConfigByFeatureId<T extends FeatureInterfaceBase>(
        clientId: number,
        featureId: number,
    ): Promise<ClientFeatureConfig<T>> {
        const clientFeatureConfig: ClientFeatureConfig<T> = await ClientFeatureConfigService.getClientFeatureConfigByFeatureId(
            clientId,
            featureId,
        );
        return clientFeatureConfig;
    }

    public async createClientFeatureConfig<T extends FeatureInterfaceBase>(
        data: NewClientFeatureConfig<T>,
        featureName: string,
    ): Promise<ClientFeatureConfig<T> | undefined> {
        const { clientId, config } = data;
        const feature: Feature | undefined = await FeatureService.getFeatureByName(featureName);
        if (feature) {
            const clientFeatureConfig: ClientFeatureConfig<T> = await ClientFeatureConfigService.createClientFeatureConfig(
                {
                    clientId,
                    config,
                    featureId: feature.id,
                },
            );

            ToastNotification({
                type: 'success',
                message: 'Success',
                description: `Feature ${featureName} has been successfully configured by client`,
            });
            return clientFeatureConfig;
        }
        ToastNotification({
            type: 'warning',
            message: 'Warning',
            description: `Feature ${featureName} configuration does not exist or is not available`,
        });

        return undefined;
    }

    public async updateClientFeatureConfigByFeatureConfigId<T extends FeatureInterfaceBase>(
        clientId: number,
        clientFeatureConfigId: number,
        config: any,
    ): Promise<ClientFeatureConfig<T>> {
        const clientFeatureConfig: ClientFeatureConfig<T> = await ClientFeatureConfigService.updateClientFeatureConfigByFeatureConfigId(
            clientId,
            clientFeatureConfigId,
            config,
        );
        ToastNotification({
            type: 'success',
            message: 'Success',
            description: 'Feature configuration has been successfully updated',
        });
        return clientFeatureConfig;
    }

    public async upsertClientFeatureConfigByFeatureConfigId<T extends FeatureInterfaceBase>(
        clientId: number,
        config: any,
        featureId: number,
        clientFeatureConfigId?: number,
    ): Promise<ClientFeatureConfig<T>> {
        const clientFeatureConfig: ClientFeatureConfig<FeatureInterfaceBase> = await ClientFeatureConfigService.upsertClientFeatureConfigByFeatureConfigId(
            clientId,
            config,
            featureId,
            clientFeatureConfigId,
        );
        ToastNotification({
            type: 'success',
            message: 'Success',
            description: 'Feature configuration has been successfully updated',
        });
        return clientFeatureConfig as ClientFeatureConfig<T>;
    }

    public async deleteClientFeatureConfigByFeatureId(clientId: number, featureId: number): Promise<void> {
        await ClientFeatureConfigService.deleteClientFeatureConfigByFeatureId(clientId, featureId);
        ToastNotification({
            type: 'success',
            message: 'Success',
            description: 'Feature configuration has been successfully deleted',
        });
    }

    public async deleteClientFeatureConfigById(clientId: number, featureConfigId: number): Promise<void> {
        await ClientFeatureConfigService.deleteClientFeatureConfigById(clientId, featureConfigId);
        ToastNotification({
            type: 'success',
            message: 'Success',
            description: 'Feature configuration has been successfully deleted',
        });
    }

    public async copyClientFeatureConfigToClients(
        clientId: number,
        configFeaturesToCopy: React.Key[],
        targettedClients: React.Key[],
    ) {
        await ClientFeatureConfigService.copyClientFeatureConfigToClients(
            clientId,
            configFeaturesToCopy,
            targettedClients,
        );
        ToastNotification({
            type: 'success',
            message: 'Success',
            description: 'Feature configurations have been created',
        });
    }
};

export default new ClientFeatureConfigController();
